import { getSupabaseClient, isSupabaseConfigured } from './supabase'

export interface NewsItem {
  id: string
  slug: string
  title: string
  category: string
  summary: string
  body: string
  publishedAt: string
  isPublished: boolean
}

export interface InquiryItem {
  id: string
  name: string
  company: string
  email: string
  subject: string
  message: string
  createdAt: string
  source: 'contact-form'
  syncStatus: 'submitted' | 'failed'
}

interface SaveNewsInput {
  id?: string
  title: string
  category: string
  summary: string
  body: string
  publishedAt: string
  isPublished: boolean
}

interface SaveInquiryInput {
  name: string
  company: string
  email: string
  subject: string
  message: string
}

interface NewsRow {
  id: string
  slug: string
  title: string
  category: string
  summary: string
  body: string
  published_at: string
  is_published: boolean
}

interface InquiryRow {
  id: string
  name: string
  company: string | null
  email: string
  subject: string | null
  message: string
  created_at: string
  source: string
  sync_status: 'submitted' | 'failed'
}

const NEWS_STORAGE_KEY = 'sakuranexus.news'
const INQUIRY_STORAGE_KEY = 'sakuranexus.inquiries'

const defaultNewsItems: NewsItem[] = [
  {
    id: 'news-launch-site',
    slug: 'site-launch',
    title: 'コーポレートサイトを公開しました',
    category: 'お知らせ',
    summary: '合同会社サクラネクサスメディアのコーポレートサイトを公開しました。',
    body: `合同会社サクラネクサスメディアのコーポレートサイトを公開しました。

商品企画、OEM製造、ECサイト運営に関する情報を、より分かりやすくお伝えできるよう順次内容を更新してまいります。

各種ご相談はお問い合わせフォームよりお気軽にお送りください。`,
    publishedAt: '2025-11-15',
    isPublished: true,
  },
]

const defaultInquiries: InquiryItem[] = []

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function readStorage<T>(key: string, fallback: T): T {
  if (!canUseStorage()) {
    return fallback
  }

  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) {
      return fallback
    }
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function writeStorage<T>(key: string, value: T) {
  if (!canUseStorage()) {
    return
  }

  window.localStorage.setItem(key, JSON.stringify(value))
}

function sortNews(items: NewsItem[]) {
  return [...items].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
}

function sortInquiries(items: InquiryItem[]) {
  return [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

function createId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }

  return `${prefix}-${Date.now()}`
}

function createSlug(value: string, publishedAt: string) {
  const normalized = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  if (normalized) {
    return normalized
  }

  return `news-${publishedAt.replaceAll('-', '')}`
}

function mapNewsRow(row: NewsRow): NewsItem {
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    category: row.category,
    summary: row.summary,
    body: row.body,
    publishedAt: row.published_at,
    isPublished: row.is_published,
  }
}

function mapInquiryRow(row: InquiryRow): InquiryItem {
  return {
    id: row.id,
    name: row.name,
    company: row.company ?? '',
    email: row.email,
    subject: row.subject ?? '',
    message: row.message,
    createdAt: row.created_at,
    source: 'contact-form',
    syncStatus: row.sync_status,
  }
}

function toNewsRow(input: SaveNewsInput, currentSlug?: string): Omit<NewsRow, 'id'> & { id?: string } {
  return {
    id: input.id,
    slug: currentSlug ?? createSlug(input.title, input.publishedAt),
    title: input.title.trim(),
    category: input.category.trim(),
    summary: input.summary.trim(),
    body: input.body.trim(),
    published_at: input.publishedAt,
    is_published: input.isPublished,
  }
}

function toInquiryRow(input: SaveInquiryInput, syncStatus: InquiryItem['syncStatus']): Omit<InquiryRow, 'id' | 'created_at'> {
  return {
    name: input.name.trim(),
    company: input.company.trim() || null,
    email: input.email.trim(),
    subject: input.subject.trim() || null,
    message: input.message.trim(),
    source: 'contact-form',
    sync_status: syncStatus,
  }
}

async function getNewsItemsFromSupabase() {
  const supabase = getSupabaseClient()
  if (!supabase) {
    return null
  }

  const { data, error } = await supabase
    .from('news_items')
    .select('id, slug, title, category, summary, body, published_at, is_published')
    .order('published_at', { ascending: false })

  if (error) {
    throw error
  }

  return (data ?? []).map((row) => mapNewsRow(row as NewsRow))
}

async function getInquiryItemsFromSupabase() {
  const supabase = getSupabaseClient()
  if (!supabase) {
    return null
  }

  const { data, error } = await supabase
    .from('inquiries')
    .select('id, name, company, email, subject, message, created_at, source, sync_status')
    .order('created_at', { ascending: false })

  if (error) {
    throw error
  }

  return (data ?? []).map((row) => mapInquiryRow(row as InquiryRow))
}

function getNewsItemsFromLocal() {
  return sortNews(readStorage(NEWS_STORAGE_KEY, defaultNewsItems))
}

function getInquiryItemsFromLocal() {
  return sortInquiries(readStorage(INQUIRY_STORAGE_KEY, defaultInquiries))
}

export function isCloudStorageEnabled() {
  return isSupabaseConfigured()
}

export function getStorageModeLabel() {
  return isSupabaseConfigured() ? 'Supabase' : 'ブラウザ保存'
}

export async function getNewsItems() {
  if (isSupabaseConfigured()) {
    try {
      return (await getNewsItemsFromSupabase()) ?? getNewsItemsFromLocal()
    } catch {
      return getNewsItemsFromLocal()
    }
  }

  return getNewsItemsFromLocal()
}

export async function getPublishedNewsItems() {
  return (await getNewsItems()).filter((item) => item.isPublished)
}

export async function getNewsItemBySlug(slug: string) {
  return (await getNewsItems()).find((item) => item.slug === slug) ?? null
}

export async function saveNewsItem(input: SaveNewsInput) {
  if (isSupabaseConfigured()) {
    const supabase = getSupabaseClient()
    if (supabase) {
      const currentItems = await getNewsItems()
      const currentItem = input.id ? currentItems.find((item) => item.id === input.id) : undefined
      const { data, error } = await supabase
        .from('news_items')
        .upsert(toNewsRow(input, currentItem?.slug), { onConflict: 'id' })
        .select('id, slug, title, category, summary, body, published_at, is_published')
        .single()

      if (error) {
        throw error
      }

      return mapNewsRow(data as NewsRow)
    }
  }

  const currentItems = getNewsItemsFromLocal()
  const nextItem: NewsItem = {
    id: input.id ?? createId('news'),
    slug: input.id
      ? currentItems.find((item) => item.id === input.id)?.slug ?? createSlug(input.title, input.publishedAt)
      : createSlug(input.title, input.publishedAt),
    title: input.title.trim(),
    category: input.category.trim(),
    summary: input.summary.trim(),
    body: input.body.trim(),
    publishedAt: input.publishedAt,
    isPublished: input.isPublished,
  }

  const nextItems = currentItems.some((item) => item.id === nextItem.id)
    ? currentItems.map((item) => (item.id === nextItem.id ? nextItem : item))
    : [nextItem, ...currentItems]

  writeStorage(NEWS_STORAGE_KEY, sortNews(nextItems))
  return nextItem
}

export async function deleteNewsItem(id: string) {
  if (isSupabaseConfigured()) {
    const supabase = getSupabaseClient()
    if (supabase) {
      const { error } = await supabase.from('news_items').delete().eq('id', id)
      if (error) {
        throw error
      }
      return
    }
  }

  const nextItems = getNewsItemsFromLocal().filter((item) => item.id !== id)
  writeStorage(NEWS_STORAGE_KEY, nextItems)
}

export async function getInquiryItems() {
  if (isSupabaseConfigured()) {
    try {
      return (await getInquiryItemsFromSupabase()) ?? getInquiryItemsFromLocal()
    } catch {
      return getInquiryItemsFromLocal()
    }
  }

  return getInquiryItemsFromLocal()
}

export async function saveInquiryItem(input: SaveInquiryInput, syncStatus: InquiryItem['syncStatus']) {
  if (isSupabaseConfigured()) {
    const supabase = getSupabaseClient()
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('inquiries')
          .insert(toInquiryRow(input, syncStatus))
          .select('id, name, company, email, subject, message, created_at, source, sync_status')
          .single()

        if (error) {
          throw error
        }

        return mapInquiryRow(data as InquiryRow)
      } catch {
        // Fall back to local storage so inquiry history remains visible in the manager.
      }
    }
  }

  const nextItem: InquiryItem = {
    id: createId('inquiry'),
    name: input.name.trim(),
    company: input.company.trim(),
    email: input.email.trim(),
    subject: input.subject.trim(),
    message: input.message.trim(),
    createdAt: new Date().toISOString(),
    source: 'contact-form',
    syncStatus,
  }

  const nextItems = [nextItem, ...getInquiryItemsFromLocal()]
  writeStorage(INQUIRY_STORAGE_KEY, nextItems)
  return nextItem
}
